/**
 * Options for `retryWithBackoff`.
 */
export interface RetryWithBackoffOptions {
  /** Maximum number of attempts (including the first). Defaults to 3. */
  maxAttempts?: number
  /** Base delay in ms, doubled after each failed attempt. Defaults to 1000. */
  baseDelayMs?: number
  /** Injectable wait function, mainly so tests can skip real delays. */
  delay?: (ms: number) => Promise<void>
  /** Called after each failed attempt with the error and attempt index (0-based). */
  onAttemptFailed?: (err: unknown, attempt: number) => void
}

const defaultDelay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Retries an async operation with exponential backoff. Rethrows the last
 * error once `maxAttempts` has been exhausted so the caller can decide how
 * to handle a permanent failure (e.g. preserve unsaved work, surface a
 * "failed to save" indicator).
 */
export async function retryWithBackoff<T>(
  fn: (attempt: number) => Promise<T>,
  options: RetryWithBackoffOptions = {},
): Promise<T> {
  const maxAttempts = options.maxAttempts ?? 3
  const baseDelayMs = options.baseDelayMs ?? 1000
  const delay = options.delay ?? defaultDelay

  let lastError: unknown

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await fn(attempt)
    } catch (err) {
      lastError = err
      options.onAttemptFailed?.(err, attempt)

      if (attempt < maxAttempts - 1) {
        await delay(baseDelayMs * 2 ** attempt)
      }
    }
  }

  throw lastError
}
